import { useState } from 'react'
import { ArrowDownTrayIcon, DocumentArrowUpIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'libs/i18n'
import { ConfirmModal } from 'shared/components'
import { cn } from 'utils/style'
import { useImportProductsMutation, RowError } from './mutations/useImportProductsMutation'

type ImportProductsModalProps = {
	isOpened: boolean
	closeModal: () => void
	bookingId: string
}

// Mirrors REQUIRED_COLUMNS/OPTIONAL_COLUMNS in gullid-mitt's
// smartgo/booking/product_csv.py — keep the two in sync if those change.
const CSV_TEMPLATE_ROWS = [
	['Product Name', 'Product Price', 'Product Quantity', 'Size', 'Custom Sku', 'Custom Barcode'],
	['T-Shirt', '2500', '3', 'M', 'SKU123', '1234567890123']
]

// Every field is quoted (RFC 4180) so spreadsheet apps don't also split on
// the spaces inside multi-word headers like "Product Name" — unquoted, some
// importers treat space as a second delimiter alongside the comma.
const toCsvRow = (row: string[]) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(',')

const downloadCsvTemplate = () => {
	const csv = CSV_TEMPLATE_ROWS.map(toCsvRow).join('\r\n') + '\r\n'
	// Leading BOM so Excel opens it as UTF-8 rather than guessing the system
	// locale's encoding; the backend parser already accepts a BOM (utf-8-sig).
	const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' })
	const url = URL.createObjectURL(blob)
	const link = document.createElement('a')
	link.href = url
	link.download = 'product_import_template.csv'
	link.click()
	URL.revokeObjectURL(url)
}

export const ImportProductsModal = ({
	isOpened,
	closeModal,
	bookingId
}: ImportProductsModalProps) => {
	const { t } = useTranslation()
	const [selectedFile, setSelectedFile] = useState<File | null>(null)
	const [rowErrors, setRowErrors] = useState<RowError[] | null>(null)

	const { importProductsMutation, isImportProductsLoading } = useImportProductsMutation({
		bookingId,
		onSuccess: () => {
			setSelectedFile(null)
			setRowErrors(null)
			closeModal()
		},
		onRowErrors: (errors) => setRowErrors(errors)
	})

	const handleClose = () => {
		setSelectedFile(null)
		setRowErrors(null)
		closeModal()
	}

	const columns = [
		{ name: 'Product Name', hint: t('Maximum of 50 characters'), required: true },
		{ name: 'Product Price', hint: t('Positive numbers only'), required: true },
		{ name: 'Product Quantity', hint: t('Positive numbers only'), required: true },
		{ name: 'Size', hint: t('Can contain letters and numbers'), required: false },
		{
			name: 'Custom Sku',
			hint: t('If left blank, Smartgo will automatically create a unique SKU for your product'),
			required: false
		},
		{
			name: 'Custom Barcode',
			hint: t('If left blank, Smartgo will automatically create a unique Barcode for your product'),
			required: false
		}
	]

	return (
		<ConfirmModal
			isOpened={isOpened}
			title={t('Import products')}
			description={t('Upload a CSV file with 1 to 50 products.')}
			confirmText={t('Import products')}
			disabled={!selectedFile}
			isLoading={isImportProductsLoading}
			closeModal={handleClose}
			confirm={() => selectedFile && importProductsMutation(selectedFile)}>
			<ul className="divide-y divide-border rounded-md border border-border">
				{columns.map((column) => (
					<li key={column.name} className="flex items-start gap-3 px-3 py-2">
						<span className="w-32 shrink-0 font-semibold text-foreground">{t(column.name)}</span>
						<span className="min-w-0 flex-1">
							{column.hint}{' '}
							<span className="whitespace-nowrap text-[11px] font-medium uppercase tracking-wider">
								({column.required ? t('Required') : t('Optional')})
							</span>
						</span>
					</li>
				))}
			</ul>

			<button
				type="button"
				className="mt-3 inline-flex items-center gap-1.5 font-medium text-primary hover:underline [&>svg]:h-4 [&>svg]:w-4"
				onClick={downloadCsvTemplate}>
				<ArrowDownTrayIcon />
				{t('Download template')}
			</button>

			<label
				className={cn(
					'mt-4 flex min-h-[96px] cursor-pointer flex-col items-center justify-center gap-1.5 rounded-md border border-dashed px-4 text-center transition-colors',
					selectedFile
						? 'border-primary bg-primary-soft text-primary'
						: 'border-border text-muted-foreground hover:border-primary/60 hover:bg-muted/50'
				)}>
				<input
					type="file"
					accept=".csv"
					className="hidden"
					onChange={(e) => {
						setRowErrors(null)
						setSelectedFile(e.target.files?.[0] ?? null)
					}}
				/>
				<DocumentArrowUpIcon className="h-5 w-5" />
				<span className="font-medium">{selectedFile ? selectedFile.name : t('Upload a file')}</span>
			</label>

			{rowErrors && (
				<ul className="mt-4 max-h-56 divide-y divide-danger/20 overflow-y-auto rounded-md border border-danger/30 bg-danger-soft">
					{rowErrors.map((rowError, index) => (
						<li key={index} className="px-3 py-2 text-danger">
							{rowError.row != null ? t('Row {{row}}', { row: rowError.row }) : t('File error')}
							{rowError.column ? `: ${rowError.column}` : ''} — {rowError.message}
						</li>
					))}
				</ul>
			)}
		</ConfirmModal>
	)
}
